/*
 * cx-photo-effects.js -- client-side photo effects for the "cx-photo"
 * customised products (Lifeline Photo Frame, Charcoal Sketch).
 *
 * 100% algorithmic canvas processing. No server endpoint, no third-party
 * AI, nothing leaves the customer's device until they add to cart (and then
 * only the flattened PNG, through the same /cart/add.js upload every other
 * customiser on this theme uses).
 *
 * Exposes window.CXPhotoFX:
 *   retroFilm(canvas, {seed})                    -> new canvas, same size
 *   oilPaint(canvas, {seed, outW, outH})         -> new canvas outW x outH
 *   sketch(canvas, {seed, style, outW, outH, workMax}) -> new canvas (ink on
 *         transparent is NOT used: returns the drawing already on paper)
 *   SKETCH_STYLES                                -> [{key,label}]
 *   fitCanvas(img, w, h, crop)                   -> canvas helper
 *
 * Every effect is deterministic for a given seed, so the on-page preview and
 * the print file show the same picture. Effects are tuned in normalised
 * units (fractions of the image size), so a 600px preview and an 1800px
 * print file look alike, only sharper.
 */
(function (global) {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Small utilities
   * ------------------------------------------------------------------ */
  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }

  function ctx2d(c) {
    return c.getContext('2d', { willReadFrequently: true });
  }

  // High-quality resample. Halves repeatedly when shrinking a lot, because
  // some mobile browsers ignore imageSmoothingQuality and alias badly on a
  // single big downscale.
  function resample(src, w, h) {
    w = Math.max(1, Math.round(w));
    h = Math.max(1, Math.round(h));
    var cur = src;
    var cw = src.width || src.naturalWidth;
    var ch = src.height || src.naturalHeight;
    while (cw / 2 >= w && ch / 2 >= h) {
      var half = makeCanvas(Math.round(cw / 2), Math.round(ch / 2));
      var hc = half.getContext('2d');
      hc.imageSmoothingEnabled = true;
      hc.imageSmoothingQuality = 'high';
      hc.drawImage(cur, 0, 0, half.width, half.height);
      cur = half;
      cw = half.width;
      ch = half.height;
    }
    var out = makeCanvas(w, h);
    var oc = out.getContext('2d');
    oc.imageSmoothingEnabled = true;
    oc.imageSmoothingQuality = 'high';
    oc.drawImage(cur, 0, 0, w, h);
    return out;
  }

  // Draw img (or canvas) into a w x h canvas using a source crop rectangle
  // {x,y,w,h} in source pixels (defaults to "cover" centred).
  function fitCanvas(img, w, h, crop) {
    var sw = img.naturalWidth || img.width;
    var sh = img.naturalHeight || img.height;
    if (!crop) {
      var targetAR = w / h;
      var cw = sw, ch = sw / targetAR;
      if (ch > sh) {
        ch = sh;
        cw = sh * targetAR;
      }
      crop = { x: (sw - cw) / 2, y: (sh - ch) / 2, w: cw, h: ch };
    }
    var tmp = makeCanvas(crop.w, crop.h);
    tmp.getContext('2d').drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, tmp.width, tmp.height);
    if (tmp.width === Math.round(w) && tmp.height === Math.round(h)) return tmp;
    return resample(tmp, w, h);
  }

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  function smoothstep(e0, e1, x) {
    var t = clamp01((x - e0) / (e1 - e0));
    return t * t * (3 - 2 * t);
  }

  // Separable running-sum box blur on a Float32 plane, in place via tmp.
  function boxBlurH(src, dst, w, h, r) {
    var iarr = 1 / (r + r + 1);
    for (var y = 0; y < h; y++) {
      var row = y * w;
      var fv = src[row];
      var val = (r + 1) * fv;
      for (var j = 0; j < r; j++) val += src[row + Math.min(j, w - 1)];
      for (var x = 0; x < w; x++) {
        var addIdx = row + (x + r > w - 1 ? w - 1 : x + r);
        var subX = x - r - 1;
        val += src[addIdx] - (subX >= 0 ? src[row + subX] : fv);
        dst[row + x] = val * iarr;
      }
    }
  }

  function boxBlurV(src, dst, w, h, r) {
    var iarr = 1 / (r + r + 1);
    for (var x = 0; x < w; x++) {
      var fv = src[x];
      var val = (r + 1) * fv;
      for (var j = 0; j < r; j++) val += src[Math.min(j, h - 1) * w + x];
      for (var y = 0; y < h; y++) {
        var addY = y + r > h - 1 ? h - 1 : y + r;
        var subY = y - r - 1;
        val += src[addY * w + x] - (subY >= 0 ? src[subY * w + x] : fv);
        dst[y * w + x] = val * iarr;
      }
    }
  }

  // Approximate gaussian: three box passes.
  function gaussBlur(plane, w, h, sigma) {
    if (sigma < 0.5) return plane.slice ? plane.slice() : new Float32Array(plane);
    var n = 3;
    var wIdeal = Math.sqrt((12 * sigma * sigma) / n + 1);
    var wl = Math.floor(wIdeal);
    if (wl % 2 === 0) wl--;
    var wu = wl + 2;
    var mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
    var m = Math.round(mIdeal);
    var a = new Float32Array(plane);
    var b = new Float32Array(plane.length);
    for (var i = 0; i < n; i++) {
      var r = ((i < m ? wl : wu) - 1) / 2;
      boxBlurH(a, b, w, h, r);
      boxBlurV(b, a, w, h, r);
    }
    return a;
  }

  function percentile(arr, p, step) {
    step = step || Math.max(1, Math.floor(arr.length / 12000));
    var sample = [];
    for (var i = 0; i < arr.length; i += step) sample.push(arr[i]);
    sample.sort(function (x, y) {
      return x - y;
    });
    return sample[Math.min(sample.length - 1, Math.floor(p * (sample.length - 1)))];
  }

  function toPlanes(canvas) {
    var w = canvas.width, h = canvas.height;
    var d = ctx2d(canvas).getImageData(0, 0, w, h).data;
    var n = w * h;
    var r = new Float32Array(n), g = new Float32Array(n), b = new Float32Array(n);
    for (var i = 0, j = 0; i < n; i++, j += 4) {
      r[i] = d[j] / 255;
      g[i] = d[j + 1] / 255;
      b[i] = d[j + 2] / 255;
    }
    return { w: w, h: h, r: r, g: g, b: b };
  }

  function luma(p) {
    var n = p.r.length;
    var L = new Float32Array(n);
    for (var i = 0; i < n; i++) L[i] = 0.299 * p.r[i] + 0.587 * p.g[i] + 0.114 * p.b[i];
    return L;
  }

  // Bilinear sample of a plane at (x, y) in plane pixel coords.
  function bilinearPlaneTo(src, sw, sh, dw, dh) {
    var out = new Float32Array(dw * dh);
    var sx = sw / dw, sy = sh / dh;
    for (var y = 0; y < dh; y++) {
      var fy = (y + 0.5) * sy - 0.5;
      if (fy < 0) fy = 0;
      var y0 = Math.floor(fy);
      if (y0 > sh - 2) y0 = sh - 2;
      if (y0 < 0) y0 = 0;
      var ty = fy - y0;
      if (ty > 1) ty = 1;
      var r0 = y0 * sw, r1 = (y0 + 1 < sh ? y0 + 1 : y0) * sw;
      for (var x = 0; x < dw; x++) {
        var fx = (x + 0.5) * sx - 0.5;
        if (fx < 0) fx = 0;
        var x0 = Math.floor(fx);
        if (x0 > sw - 2) x0 = sw - 2;
        if (x0 < 0) x0 = 0;
        var tx = fx - x0;
        if (tx > 1) tx = 1;
        var x1 = x0 + 1 < sw ? x0 + 1 : x0;
        var a = src[r0 + x0] + (src[r0 + x1] - src[r0 + x0]) * tx;
        var c = src[r1 + x0] + (src[r1 + x1] - src[r1 + x0]) * tx;
        out[y * dw + x] = a + (c - a) * ty;
      }
    }
    return out;
  }

  function sampleGrid(grid, gw, gh, w, h, cell) {
    var out = new Float32Array(w * h);
    for (var y = 0; y < h; y++) {
      var fy = y / cell;
      var y0 = Math.floor(fy), ty = fy - y0;
      ty = ty * ty * (3 - 2 * ty);
      for (var x = 0; x < w; x++) {
        var fx = x / cell;
        var x0 = Math.floor(fx), tx = fx - x0;
        tx = tx * tx * (3 - 2 * tx);
        var i00 = y0 * gw + x0;
        var a = grid[i00] + (grid[i00 + 1] - grid[i00]) * tx;
        var b = grid[i00 + gw] + (grid[i00 + gw + 1] - grid[i00 + gw]) * tx;
        out[y * w + x] = a + (b - a) * ty;
      }
    }
    return out;
  }

  // Fine grain canvas (mid-grey +- noise) at a fixed "film" resolution,
  // stretched to the target so grain size is relative to the picture, not
  // to the pixel count (the print file and the preview look alike).
  function grainCanvas(w, h, rnd, amp, baseRes) {
    var scale = Math.min(1, baseRes / Math.max(w, h));
    var gw = Math.max(8, Math.round(w * scale)), gh = Math.max(8, Math.round(h * scale));
    var c = makeCanvas(gw, gh);
    var cx = c.getContext('2d');
    var id = cx.createImageData(gw, gh);
    var d = id.data;
    for (var i = 0; i < d.length; i += 4) {
      var n = (rnd() + rnd() + rnd() - 1.5) * amp;
      var v = 128 + n;
      d[i] = d[i + 1] = d[i + 2] = v;
      d[i + 3] = 255;
    }
    cx.putImageData(id, 0, 0);
    return c;
  }

  /* ------------------------------------------------------------------ *
   * Retro Film
   * Warm grade, lifted blacks, rolled-off highlights, gentle desaturation,
   * soft halation around highlights, one soft light leak, slight vignette,
   * fine grain.
   * ------------------------------------------------------------------ */
  function retroFilm(src, opts) {
    opts = opts || {};
    var rnd = mulberry32((opts.seed || 11) * 2654435761);
    var w = src.width, h = src.height;
    var out = makeCanvas(w, h);
    var ctx = ctx2d(out);
    ctx.drawImage(src, 0, 0);

    // 1. Tone curves (per channel LUTs)
    var lr = new Float32Array(256), lg = new Float32Array(256), lb = new Float32Array(256);
    for (var i = 0; i < 256; i++) {
      var x = i / 255;
      var s = x * 0.62 + 0.38 * (x * x * (3 - 2 * x)); // soft S
      var mid = Math.sin(Math.PI * s); // 0 at ends, 1 at mid
      lr[i] = 255 * clamp01(0.085 + s * (0.975 - 0.085) + 0.035 * mid);
      lg[i] = 255 * clamp01(0.07 + s * (0.945 - 0.07) + 0.012 * mid);
      lb[i] = 255 * clamp01(0.085 + s * (0.865 - 0.085) - 0.03 * mid);
    }
    var id = ctx.getImageData(0, 0, w, h);
    var d = id.data;
    var sat = 0.82;
    for (var p = 0; p < d.length; p += 4) {
      var r = d[p], g = d[p + 1], b = d[p + 2];
      var Y = 0.299 * r + 0.587 * g + 0.114 * b;
      r = Y + (r - Y) * sat;
      g = Y + (g - Y) * sat;
      b = Y + (b - Y) * sat;
      d[p] = lr[r < 0 ? 0 : r > 255 ? 255 : r | 0];
      d[p + 1] = lg[g < 0 ? 0 : g > 255 ? 255 : g | 0];
      d[p + 2] = lb[b < 0 ? 0 : b > 255 ? 255 : b | 0];
    }
    ctx.putImageData(id, 0, 0);

    // 2. Halation: blurred highlights, tinted warm-red, screened back in.
    var smallW = Math.max(8, Math.round(w / 12)), smallH = Math.max(8, Math.round(h / 12));
    var small = resample(out, smallW, smallH);
    var sc = ctx2d(small);
    var sid = sc.getImageData(0, 0, smallW, smallH);
    var sd = sid.data;
    for (var q = 0; q < sd.length; q += 4) {
      var Yq = (0.299 * sd[q] + 0.587 * sd[q + 1] + 0.114 * sd[q + 2]) / 255;
      var v = smoothstep(0.62, 0.95, Yq);
      sd[q] = 255 * v;
      sd[q + 1] = 120 * v;
      sd[q + 2] = 70 * v;
      sd[q + 3] = 255;
    }
    sc.putImageData(sid, 0, 0);
    var blurred = resample(resample(small, Math.max(4, smallW / 3), Math.max(4, smallH / 3)), w, h);
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = 0.32;
    ctx.drawImage(blurred, 0, 0);
    ctx.restore();

    // 3. Light leak from one edge (seeded side + position).
    var side = rnd() < 0.5 ? 0 : 1; // 0 = left edge, 1 = right edge
    var ly = h * (0.08 + rnd() * 0.35);
    var lx = side === 0 ? -w * 0.12 : w * 1.12;
    var lrad = Math.max(w, h) * 0.78;
    var grad = ctx.createRadialGradient(lx, ly, 0, lx, ly, lrad);
    grad.addColorStop(0, 'rgba(255,92,28,0.85)');
    grad.addColorStop(0.3, 'rgba(255,124,44,0.5)');
    grad.addColorStop(0.6, 'rgba(255,176,96,0.12)');
    grad.addColorStop(1, 'rgba(255,190,120,0)');
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
    // a thin, fainter streak along the same edge
    var sx0 = side === 0 ? 0 : w;
    var streak = ctx.createLinearGradient(sx0, 0, side === 0 ? w * 0.16 : w * 0.84, 0);
    streak.addColorStop(0, 'rgba(255,150,70,0.38)');
    streak.addColorStop(1, 'rgba(255,150,70,0)');
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = streak;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 4. Vignette
    var cxp = w / 2, cyp = h / 2;
    var diag = Math.sqrt(w * w + h * h) / 2;
    var vg = ctx.createRadialGradient(cxp, cyp, diag * 0.42, cxp, cyp, diag * 1.02);
    vg.addColorStop(0, 'rgba(255,255,255,1)');
    vg.addColorStop(1, 'rgba(150,120,100,1)');
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 5. Grain
    var gc = grainCanvas(w, h, rnd, 36, 900);
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.42;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(gc, 0, 0, w, h);
    ctx.restore();

    return out;
  }

  /* ------------------------------------------------------------------ *
   * Oil Painting
   * Layered curved brush strokes (Hertzmann-style painterly rendering):
   * big strokes first, then progressively smaller ones only where the
   * painting still differs from the photo (eyes, mouth, edges keep detail;
   * flat areas stay loose). Strokes are planned at a fixed working size and
   * replayed as vectors at the output size, so the print file is crisp.
   * Finished with bristle streaks, a soft impasto relief and canvas weave.
   * ------------------------------------------------------------------ */
  function* planOilGen(src, seed, workMax) {
    var rnd = mulberry32((seed || 5) * 2246822519);
    var scale = workMax / Math.max(src.width, src.height);
    var ww = Math.round(src.width * scale), wh = Math.round(src.height * scale);
    var work = resample(src, ww, wh);
    var ref = toPlanes(work);
    var n = ww * wh;

    // The "painting so far" is simulated at half resolution: it only has to
    // tell us where the picture still differs from the photo, and a quarter
    // of the pixels makes the raster + read-back four times cheaper.
    var PS = 0.5;
    var pw = Math.max(8, Math.round(ww * PS)), ph = Math.max(8, Math.round(wh * PS));
    var paint = makeCanvas(pw, ph);
    var pctx = ctx2d(paint);
    pctx.lineCap = 'round';
    pctx.lineJoin = 'round';

    var base = workMax / 640;
    // Smoothed structure tensor -> a coherent stroke-direction field, so
    // brush strokes flow along forms (hair, cloth folds, face contours)
    // instead of jittering with pixel noise.
    var L0 = gaussBlur(luma(ref), ww, wh, 1.2 * base);
    var Jxx = new Float32Array(n), Jxy = new Float32Array(n), Jyy = new Float32Array(n);
    for (var ty = 1; ty < wh - 1; ty++) {
      for (var tx = 1; tx < ww - 1; tx++) {
        var t0 = ty * ww + tx;
        var sgx = L0[t0 - ww + 1] + 2 * L0[t0 + 1] + L0[t0 + ww + 1] - L0[t0 - ww - 1] - 2 * L0[t0 - 1] - L0[t0 + ww - 1];
        var sgy = L0[t0 + ww - 1] + 2 * L0[t0 + ww] + L0[t0 + ww + 1] - L0[t0 - ww - 1] - 2 * L0[t0 - ww] - L0[t0 - ww + 1];
        Jxx[t0] = sgx * sgx;
        Jxy[t0] = sgx * sgy;
        Jyy[t0] = sgy * sgy;
      }
    }
    var tsig = 3.2 * base;
    Jxx = gaussBlur(Jxx, ww, wh, tsig);
    Jxy = gaussBlur(Jxy, ww, wh, tsig);
    Jyy = gaussBlur(Jyy, ww, wh, tsig);
    var TX = new Float32Array(n), TY = new Float32Array(n), TS = new Float32Array(n);
    for (var tt = 0; tt < n; tt++) {
      var a2 = Jxx[tt], b2 = Jxy[tt], c2 = Jyy[tt];
      var theta = 0.5 * Math.atan2(2 * b2, a2 - c2); // gradient orientation
      TX[tt] = -Math.sin(theta);
      TY[tt] = Math.cos(theta);
      TS[tt] = a2 + c2;
    }
    var radii = [14 * base, 7.5 * base, 4.2 * base, 2.5 * base];
    var thresholds = [0, 0.075, 0.065, 0.065];
    var layers = [];

    for (var li = 0; li < radii.length; li++) {
      var R = radii[li];
      var sigma = Math.max(0.6, R * 0.5);
      var br = gaussBlur(ref.r, ww, wh, sigma);
      var bg = gaussBlur(ref.g, ww, wh, sigma);
      var bb = gaussBlur(ref.b, ww, wh, sigma);
      var L = new Float32Array(n);
      for (var i = 0; i < n; i++) L[i] = 0.299 * br[i] + 0.587 * bg[i] + 0.114 * bb[i];

      var cur = null;
      if (li > 0) {
        var cd = pctx.getImageData(0, 0, pw, ph).data;
        cur = new Float32Array(n * 3);
        for (var cy = 0; cy < wh; cy++) {
          var py = Math.min(ph - 1, Math.floor(cy * PS));
          for (var cx = 0; cx < ww; cx++) {
            var kk = (py * pw + Math.min(pw - 1, Math.floor(cx * PS))) * 4;
            var k = (cy * ww + cx) * 3;
            cur[k] = cd[kk] / 255;
            cur[k + 1] = cd[kk + 1] / 255;
            cur[k + 2] = cd[kk + 2] / 255;
          }
        }
      }

      function errAt(idx) {
        if (!cur) return 1;
        var dr = cur[idx * 3] - br[idx], dg = cur[idx * 3 + 1] - bg[idx], db = cur[idx * 3 + 2] - bb[idx];
        return Math.sqrt(dr * dr + dg * dg + db * db);
      }

      var strokes = [];
      var step = Math.max(1, Math.round(R));
      var maxLen = li === 0 ? 8 : li === 1 ? 10 : li === 2 ? 10 : 6;
      var minLen = li === 0 ? 3 : li === 3 ? 1 : 3;
      for (var gy0 = 0; gy0 < wh; gy0 += step) {
        for (var gx0 = 0; gx0 < ww; gx0 += step) {
          // jitter the grid a little so strokes don't line up
          var jx = Math.min(ww - 1, gx0 + Math.floor(rnd() * step));
          var jy = Math.min(wh - 1, gy0 + Math.floor(rnd() * step));
          var sx, sy;
          if (cur) {
            var sum = 0, best = -1, bx = jx, by = jy, cnt = 0;
            for (var yy = gy0; yy < Math.min(wh, gy0 + step); yy++) {
              for (var xx = gx0; xx < Math.min(ww, gx0 + step); xx++) {
                var e = errAt(yy * ww + xx);
                sum += e;
                cnt++;
                if (e > best) {
                  best = e;
                  bx = xx;
                  by = yy;
                }
              }
            }
            if (sum / cnt < thresholds[li]) continue;
            sx = bx;
            sy = by;
          } else {
            sx = jx;
            sy = jy;
          }
          var si = sy * ww + sx;
          var col = [br[si], bg[si], bb[si]];
          var pts = [sx, sy];
          var x = sx, y = sy, ldx = 0, ldy = 0;
          for (var s = 1; s <= maxLen; s++) {
            var ix = Math.round(x), iy = Math.round(y);
            if (ix < 0 || iy < 0 || ix >= ww || iy >= wh) break;
            var ii = iy * ww + ix;
            if (s > minLen && cur) {
              var dc = errAt(ii);
              var dr2 = col[0] - br[ii], dg2 = col[1] - bg[ii], db2 = col[2] - bb[ii];
              var ds = Math.sqrt(dr2 * dr2 + dg2 * dg2 + db2 * db2);
              if (dc < ds) break;
            } else if (s > minLen) {
              var dr3 = col[0] - br[ii], dg3 = col[1] - bg[ii], db3 = col[2] - bb[ii];
              if (Math.sqrt(dr3 * dr3 + dg3 * dg3 + db3 * db3) > 0.16) break;
            }
            var gm = TS[ii];
            var dx, dy;
            if (gm < 1e-5) {
              if (s === 1) {
                var a0 = rnd() * Math.PI;
                dx = Math.cos(a0);
                dy = Math.sin(a0);
              } else {
                dx = ldx;
                dy = ldy;
              }
            } else {
              dx = TX[ii];
              dy = TY[ii];
              if (ldx * dx + ldy * dy < 0) {
                dx = -dx;
                dy = -dy;
              }
              if (s > 1) {
                dx = 0.6 * dx + 0.4 * ldx;
                dy = 0.6 * dy + 0.4 * ldy;
                var m = Math.sqrt(dx * dx + dy * dy) || 1;
                dx /= m;
                dy /= m;
              }
            }
            x += R * dx;
            y += R * dy;
            pts.push(x, y);
            ldx = dx;
            ldy = dy;
          }
          // colour jitter: a touch of value/temperature variation per stroke
          var jv = (rnd() - 0.5) * 0.06;
          var jt = (rnd() - 0.5) * 0.03;
          strokes.push({
            pts: pts,
            r: R,
            c: [clamp01(col[0] + jv + jt), clamp01(col[1] + jv), clamp01(col[2] + jv - jt)],
            seed: rnd(),
          });
        }
      }
      // shuffle so stroke order doesn't reveal the grid
      for (var z = strokes.length - 1; z > 0; z--) {
        var zj = Math.floor(rnd() * (z + 1));
        var tmp = strokes[z];
        strokes[z] = strokes[zj];
        strokes[zj] = tmp;
      }
      if (li === 0) {
        // underpainting: mean colour so no gaps show between first strokes
        var mr = 0, mg = 0, mb = 0;
        for (var u = 0; u < n; u += 7) {
          mr += br[u];
          mg += bg[u];
          mb += bb[u];
        }
        var cntU = Math.ceil(n / 7);
        pctx.fillStyle = rgbStr([mr / cntU, mg / cntU, mb / cntU]);
        pctx.fillRect(0, 0, pw, ph);
      }
      if (li < radii.length - 1) drawStrokes(pctx, strokes, PS, false);
      layers.push(strokes);
      yield strokes;
    }
    return { w: ww, h: wh, layers: layers };
  }

  function planOilStrokes(src, seed, workMax) {
    var g = planOilGen(src, seed, workMax);
    var r = g.next();
    while (!r.done) r = g.next();
    return r.value;
  }

  function rgbStr(c, k) {
    k = k || 0;
    return (
      'rgb(' +
      Math.round(255 * clamp01(c[0] + k)) +
      ',' +
      Math.round(255 * clamp01(c[1] + k)) +
      ',' +
      Math.round(255 * clamp01(c[2] + k)) +
      ')'
    );
  }

  function pathFor(ctx, pts, s) {
    ctx.beginPath();
    ctx.moveTo(pts[0] * s, pts[1] * s);
    if (pts.length === 2) {
      ctx.lineTo(pts[0] * s + 0.01, pts[1] * s + 0.01);
      return;
    }
    for (var i = 2; i < pts.length - 2; i += 2) {
      var mx = ((pts[i] + pts[i + 2]) / 2) * s, my = ((pts[i + 1] + pts[i + 3]) / 2) * s;
      ctx.quadraticCurveTo(pts[i] * s, pts[i + 1] * s, mx, my);
    }
    ctx.lineTo(pts[pts.length - 2] * s, pts[pts.length - 1] * s);
  }

  function drawStrokes(ctx, strokes, s, bristles) {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (var i = 0; i < strokes.length; i++) {
      var st = strokes[i];
      ctx.strokeStyle = rgbStr(st.c);
      ctx.lineWidth = st.r * 2 * s;
      pathFor(ctx, st.pts, s);
      ctx.stroke();
      if (bristles && st.pts.length > 3 && st.seed < 0.6) {
        // two thin streaks inside the stroke: one lighter, one darker
        var off = st.r * 0.42 * s;
        var p = st.pts;
        var dx = p[p.length - 2] - p[0], dy = p[p.length - 1] - p[1];
        var m = Math.sqrt(dx * dx + dy * dy) || 1;
        var nx = (-dy / m) * off, ny = (dx / m) * off;
        var k = st.seed < 0.5 ? 1 : -1;
        ctx.lineWidth = Math.max(0.6, st.r * 0.32 * s);
        ctx.globalAlpha = 0.38;
        ctx.save();
        ctx.translate(nx * k, ny * k);
        ctx.strokeStyle = rgbStr(st.c, 0.07);
        pathFor(ctx, st.pts, s);
        ctx.stroke();
        ctx.restore();
        ctx.save();
        ctx.translate(-nx * k * 0.8, -ny * k * 0.8);
        ctx.strokeStyle = rgbStr(st.c, -0.07);
        pathFor(ctx, st.pts, s);
        ctx.stroke();
        ctx.restore();
        ctx.globalAlpha = 1;
      }
    }
  }

  var oilPlanCache = { key: null, plan: null };

  function oilKey(src, opts) {
    return (src.__cxId || '') + ':' + (opts.seed || 5) + ':' + (opts.workMax || 520);
  }

  function oilBase(src, outW, outH) {
    var out = makeCanvas(outW, outH);
    var ctx = ctx2d(out);
    // underpainting = heavily blurred photo, so nothing shows through gaps
    ctx.drawImage(resample(resample(src, Math.max(8, outW / 6), Math.max(8, outH / 6)), Math.max(8, outW / 24), Math.max(8, outH / 24)), 0, 0, outW, outH);
    return out;
  }

  function oilLayerHasBristles(li) {
    return li === 1 || li === 2;
  }

  function oilFinish(out, s, seed) {
    var ctx = ctx2d(out);
    var outW = out.width, outH = out.height;
    // Impasto relief: emboss the luminance of the painted strokes.
    var id = ctx.getImageData(0, 0, outW, outH);
    var d = id.data;
    var n = outW * outH;
    var L = new Float32Array(n);
    for (var i = 0, j = 0; i < n; i++, j += 4) L[i] = (0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2]) / 255;
    var Hs = gaussBlur(L, outW, outH, Math.max(0.8, s * 1.1));
    var off = Math.max(1, Math.round(s * 1.0));
    var strength = 0.9;
    for (var y = off; y < outH - off; y++) {
      for (var x = off; x < outW - off; x++) {
        var idx = y * outW + x;
        var add = (Hs[idx - off * outW - off] - Hs[idx + off * outW + off]) * strength * 255 * 0.55;
        var p = idx * 4;
        // a touch more saturation + warmth, like varnished oils
        var r = d[p] + add, g = d[p + 1] + add, b = d[p + 2] + add;
        var Y = 0.299 * r + 0.587 * g + 0.114 * b;
        d[p] = Y + (r - Y) * 1.12 + 3;
        d[p + 1] = Y + (g - Y) * 1.12;
        d[p + 2] = Y + (b - Y) * 1.12 - 4;
      }
    }
    ctx.putImageData(id, 0, 0);
    // Canvas weave: fine crosshatch texture, overlay-blended.
    var weave = weaveCanvas(outW, outH, seed);
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.28;
    ctx.drawImage(weave, 0, 0, outW, outH);
    ctx.restore();
    return out;
  }

  // Synchronous: used for the print file (plan is normally cached already
  // from the preview, so only the vector replay at print size runs).
  function oilPaint(src, opts) {
    opts = opts || {};
    var key = oilKey(src, opts);
    var plan = src.__cxId && oilPlanCache.key === key ? oilPlanCache.plan : null;
    if (!plan) {
      plan = planOilStrokes(src, opts.seed || 5, opts.workMax || 520);
      if (src.__cxId) {
        oilPlanCache.key = key;
        oilPlanCache.plan = plan;
      }
    }
    var outW = Math.round(opts.outW || src.width), outH = Math.round(opts.outH || src.height);
    var s = outW / plan.w;
    var out = oilBase(src, outW, outH);
    var ctx = ctx2d(out);
    for (var li = 0; li < plan.layers.length; li++) drawStrokes(ctx, plan.layers[li], s, oilLayerHasBristles(li));
    return oilFinish(out, s, opts.seed || 5);
  }

  // Progressive: paints layer by layer, calling onStep(canvasSoFar) and
  // yielding to the browser between layers, so the customer watches the
  // portrait being painted (big strokes first, details last) instead of
  // staring at a spinner.
  function oilPaintProgressive(src, opts, onStep) {
    opts = opts || {};
    var key = oilKey(src, opts);
    if (src.__cxId && oilPlanCache.key === key) return Promise.resolve(oilPaint(src, opts));
    var seed = opts.seed || 5;
    var outW = Math.round(opts.outW || src.width), outH = Math.round(opts.outH || src.height);
    var gen = planOilGen(src, seed, opts.workMax || 520);
    var out = oilBase(src, outW, outH);
    var ctx = ctx2d(out);
    var li = 0;
    var s = null;
    return new Promise(function (resolve, reject) {
      function step() {
        try {
          var r = gen.next();
          if (r.done) {
            if (src.__cxId) {
              oilPlanCache.key = key;
              oilPlanCache.plan = r.value;
            }
            resolve(oilFinish(out, outW / r.value.w, seed));
            return;
          }
          if (s === null) s = outW / Math.round(src.width * ((opts.workMax || 520) / Math.max(src.width, src.height)));
          drawStrokes(ctx, r.value, s, oilLayerHasBristles(li));
          li += 1;
          if (onStep) onStep(out, li);
          requestAnimationFrame(function () {
            setTimeout(step, 0);
          });
        } catch (e) {
          reject(e);
        }
      }
      step();
    });
  }

  function weaveCanvas(w, h, seed) {
    var rnd = mulberry32(seed * 7 + 3);
    var base = 900;
    var scale = Math.min(1, base / Math.max(w, h));
    var cw = Math.round(w * scale), ch = Math.round(h * scale);
    var c = makeCanvas(cw, ch);
    var cx = c.getContext('2d');
    var id = cx.createImageData(cw, ch);
    var d = id.data;
    var period = 3.2;
    for (var y = 0; y < ch; y++) {
      for (var x = 0; x < cw; x++) {
        var a = Math.sin((x / period) * Math.PI * 2) * 0.5 + 0.5;
        var b = Math.sin((y / period) * Math.PI * 2) * 0.5 + 0.5;
        var v = 128 + (a * b - 0.25) * 70 + (rnd() - 0.5) * 30;
        var p = (y * cw + x) * 4;
        d[p] = d[p + 1] = d[p + 2] = v;
        d[p + 3] = 255;
      }
    }
    cx.putImageData(id, 0, 0);
    return c;
  }

  /* ------------------------------------------------------------------ *
   * Charcoal / pencil sketch
   * 1. Outline strokes: gradient magnitude, classified into 8 directions,
   *    each class smeared along its own direction (Lu, Xu & Jia 2012
   *    "Combining Sketch and Tone for Pencil Drawing Production"). Gives
   *    hand-drawn lines that slightly overshoot, like a real sketch.
   * 2. Tone: hatching layers that switch on progressively as the photo
   *    gets darker, drawn as real wobbly lines at output resolution.
   * 3. Paper: cream sheet with tooth/grain that breaks up the graphite.
   * ------------------------------------------------------------------ */
  var SKETCH_STYLES = [
    { key: 'soft', label: 'Soft shading' },
    { key: 'pencil', label: 'Pencil sketch' },
    { key: 'charcoal', label: 'Bold charcoal' },
    { key: 'crosshatch', label: 'Cross-hatch' },
  ];

  var STYLE_PARAMS = {
    pencil: { detailGain: 2.6, strokeMix: 0.7, lineLen: 1 / 42, lineGain: 0.95, lineGamma: 1.2, hatch: [0.34, 0.58, 0.8], hatchGain: 0.38, smudge: 0.35, ink: [58, 56, 62], inkMax: 0.88, spacing: 1 / 150, toneGain: 0.62, lineW: 1 / 760 },
    charcoal: { detailGain: 3.0, strokeMix: 0.85, lineLen: 1 / 34, lineGain: 1.2, lineGamma: 0.95, hatch: [0.3, 0.55, 0.78], hatchGain: 0.3, smudge: 0.8, ink: [30, 27, 26], inkMax: 0.96, spacing: 1 / 105, toneGain: 0.95, lineW: 1 / 400 },
    crosshatch: { detailGain: 2.8, strokeMix: 0.8, lineLen: 1 / 46, lineGain: 1.0, lineGamma: 1.2, hatch: [0.24, 0.44, 0.64], hatchGain: 0.8, smudge: 0.0, ink: [36, 34, 44], inkMax: 0.92, spacing: 1 / 135, toneGain: 0.15, lineW: 1 / 620 },
    soft: { detailGain: 2.2, strokeMix: 0.5, lineLen: 1 / 50, lineGain: 0.8, lineGamma: 1.35, hatch: [0.4, 0.65, 0.85], hatchGain: 0.18, smudge: 0.7, ink: [72, 68, 72], inkMax: 0.85, spacing: 1 / 170, toneGain: 0.75, lineW: 1 / 820 },
  };


  function lineConvolve(src, w, h, dx, dy, half, out, stride) {
    // out += sum over t in [-half, half] (step stride) of src shifted by round(t*dx, t*dy)
    stride = stride || 1;
    for (var t = -half; t <= half; t += stride) {
      var ox = Math.round(t * dx), oy = Math.round(t * dy);
      var x0 = Math.max(0, -ox), x1 = Math.min(w, w - ox);
      var y0 = Math.max(0, -oy), y1 = Math.min(h, h - oy);
      for (var y = y0; y < y1; y++) {
        var row = y * w, srow = (y + oy) * w + ox;
        for (var x = x0; x < x1; x++) out[row + x] += src[srow + x];
      }
    }
  }

  function sketchPlanes(src, styleKey, workMax) {
    var P = STYLE_PARAMS[styleKey] || STYLE_PARAMS.pencil;
    var scale = Math.min(1, workMax / Math.max(src.width, src.height));
    var ww = Math.round(src.width * scale), wh = Math.round(src.height * scale);
    var work = resample(src, ww, wh);
    var planes = toPlanes(work);
    var n = ww * wh;
    var L = luma(planes);

    // auto-levels (1st..99th percentile) + mild gamma so faces sit mid-tone
    var lo = percentile(L, 0.01), hi = percentile(L, 0.99);
    var span = Math.max(0.05, hi - lo);
    for (var i = 0; i < n; i++) L[i] = Math.pow(clamp01((L[i] - lo) / span), 0.92);

    var dim = Math.max(ww, wh);
    // gentle local-contrast lift: bright, flat faces (wedding photos,
    // flash selfies) still get enough shading to read as a drawing
    var Lm = gaussBlur(L, ww, wh, dim / 14);
    for (var lc = 0; lc < n; lc++) L[lc] = clamp01(0.5 + (L[lc] - Lm[lc]) * 1.35 + (Lm[lc] - 0.5) * 0.75);
    var Ls = gaussBlur(L, ww, wh, Math.max(0.7, dim / 600));

    // gradient magnitude
    var G = new Float32Array(n);
    for (var y = 1; y < wh - 1; y++) {
      for (var x = 1; x < ww - 1; x++) {
        var k = y * ww + x;
        var gx = Ls[k - ww + 1] + 2 * Ls[k + 1] + Ls[k + ww + 1] - Ls[k - ww - 1] - 2 * Ls[k - 1] - Ls[k + ww - 1];
        var gy = Ls[k + ww - 1] + 2 * Ls[k + ww] + Ls[k + ww + 1] - Ls[k - ww - 1] - 2 * Ls[k - ww] - Ls[k - ww + 1];
        G[k] = Math.sqrt(gx * gx + gy * gy);
      }
    }
    var g95 = percentile(G, 0.97) || 1;
    for (var j = 0; j < n; j++) {
      var gv = G[j] / g95;
      G[j] = gv > 1 ? 1 : smoothstep(0.06, 1, gv) * gv;
    }

    // classify into 8 directions with a short line kernel, then smear
    var half = Math.max(2, Math.round((dim * P.lineLen) / 2));
    var halfC = Math.max(2, Math.round(half * 0.6));
    var dirs = 8;
    var resp = [];
    var dxs = [], dys = [];
    for (var d = 0; d < dirs; d++) {
      var th = (d * Math.PI) / dirs;
      dxs.push(Math.cos(th));
      dys.push(Math.sin(th));
      var r = new Float32Array(n);
      lineConvolve(G, ww, wh, dxs[d], dys[d], halfC, r, 2);
      resp.push(r);
    }
    var cls = new Uint8Array(n);
    for (var q = 0; q < n; q++) {
      var best = -1, bi = 0;
      for (var e = 0; e < dirs; e++) {
        var rv = resp[e][q];
        if (rv > best) {
          best = rv;
          bi = e;
        }
      }
      cls[q] = bi;
    }
    resp = null;
    var S = new Float32Array(n);
    var C = new Float32Array(n);
    for (var d2 = 0; d2 < dirs; d2++) {
      for (var q2 = 0; q2 < n; q2++) C[q2] = cls[q2] === d2 ? G[q2] : 0;
      lineConvolve(C, ww, wh, dxs[d2], dys[d2], half, S, 1);
    }
    var s99 = percentile(S, 0.985) || 1;
    for (var m = 0; m < n; m++) {
      var sv = clamp01((S[m] / s99) * P.lineGain);
      S[m] = Math.pow(sv, P.lineGamma);
    }

    // Detail lines ("colour dodge" pencil trick): how much darker a pixel
    // is than its neighbourhood. Catches eyes, lashes, nostrils, lips and
    // hair strands crisply -- the features people recognise a face by.
    var Ld = gaussBlur(L, ww, wh, Math.max(0.8, dim / 380));
    var Lb = gaussBlur(Ld, ww, wh, Math.max(1.2, dim / 90));
    var DET = new Float32Array(n);
    for (var dd = 0; dd < n; dd++) {
      var rel = 1 - Ld[dd] / (Lb[dd] + 0.03);
      DET[dd] = smoothstep(0.035, 0.035 + 1 / P.detailGain, rel);
    }
    DET = gaussBlur(DET, ww, wh, Math.max(0.5, dim / 1400));

    // tone darkness (0 = paper, 1 = darkest). Sketches leave highlights bare.
    var T = gaussBlur(L, ww, wh, Math.max(0.7, dim / 420));
    var D = new Float32Array(n);
    for (var t = 0; t < n; t++) D[t] = smoothstep(0.1, 0.92, 1 - T[t]);
    var Dsm = P.smudge > 0 ? gaussBlur(D, ww, wh, Math.max(1, dim / 160)) : D;
    if (P.smudge > 0) for (var u = 0; u < n; u++) Dsm[u] = D[u] * (1 - P.smudge) + Dsm[u] * P.smudge;
    // combine outline strokes with detail lines into one "line" plane
    for (var v2 = 0; v2 < n; v2++) S[v2] = 1 - (1 - S[v2] * P.strokeMix) * (1 - DET[v2]);
    return { w: ww, h: wh, S: S, D: Dsm, P: P };
  }

  // Draw one hatch layer: long, slightly wavy, pressure-varying lines.
  function hatchLayer(w, h, angle, spacing, lineW, rnd, jitterAmt) {
    var c = makeCanvas(w, h);
    var ctx = c.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.lineCap = 'round';
    var diag = Math.sqrt(w * w + h * h);
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(angle);
    var segLen = diag / 10;
    for (var off = -diag / 2; off < diag / 2; off += spacing * (0.75 + rnd() * 0.5)) {
      // each line is broken into a few strokes, like a hand lifting the pencil
      var x = -diag / 2 + rnd() * segLen;
      while (x < diag / 2) {
        var len = segLen * (0.6 + rnd() * 1.6);
        var a = 0.45 + rnd() * 0.55;
        ctx.strokeStyle = 'rgba(255,255,255,' + a.toFixed(3) + ')';
        ctx.lineWidth = lineW * (0.7 + rnd() * 0.7);
        ctx.beginPath();
        var y = off + (rnd() - 0.5) * spacing * 0.35;
        ctx.moveTo(x, y);
        var cx1 = x + len / 2, cy1 = y + (rnd() - 0.5) * spacing * jitterAmt;
        ctx.quadraticCurveTo(cx1, cy1, x + len, y + (rnd() - 0.5) * spacing * 0.4);
        ctx.stroke();
        x += len + segLen * (0.04 + rnd() * 0.18);
      }
    }
    ctx.restore();
    var d = c.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, w, h).data;
    var out = new Float32Array(w * h);
    for (var i = 0, j = 0; i < out.length; i++, j += 4) out[i] = d[j] / 255;
    return out;
  }

  var sketchPlanCache = { key: null, plan: null };

  function sketch(src, opts) {
    opts = opts || {};
    var styleKey = opts.style || 'pencil';
    var seed = opts.seed || 1;
    var outW = Math.round(opts.outW || src.width), outH = Math.round(opts.outH || src.height);
    var workMax = Math.min(opts.workMax || 900, Math.max(outW, outH));
    var key = (src.__cxId || '') + ':' + styleKey + ':' + workMax;
    var plan = src.__cxId && sketchPlanCache.key === key ? sketchPlanCache.plan : null;
    if (!plan) {
      plan = sketchPlanes(src, styleKey, workMax);
      if (src.__cxId) {
        sketchPlanCache.key = key;
        sketchPlanCache.plan = plan;
      }
    }
    var P = plan.P;
    var rnd = mulberry32(seed * 40503 + 17);
    var n = outW * outH;
    var dim = Math.max(outW, outH);

    var S = bilinearPlaneTo(plan.S, plan.w, plan.h, outW, outH);
    var D = bilinearPlaneTo(plan.D, plan.w, plan.h, outW, outH);

    // hatch directions vary with the seed ("Try another style" re-rolls)
    var baseAngle = (styleKey === 'crosshatch' ? -Math.PI / 4 : -Math.PI / 3.2) + (rnd() - 0.5) * 0.5;
    var angles = [baseAngle, baseAngle + Math.PI / 2 + (rnd() - 0.5) * 0.3, baseAngle + Math.PI / 4 + (rnd() - 0.5) * 0.3];
    var spacing = dim * P.spacing;
    var lineW = Math.max(0.8, dim * P.lineW);
    var H1 = hatchLayer(outW, outH, angles[0], spacing, lineW, rnd, 0.6);
    var H2 = hatchLayer(outW, outH, angles[1], spacing * 1.05, lineW, rnd, 0.6);
    var H3 = hatchLayer(outW, outH, angles[2], spacing * 0.9, lineW, rnd, 0.6);

    // paper tooth: fine grain + softer fibres
    var tooth = grainPlane(outW, outH, rnd, 1100);
    var fib = sampleNoise(outW, outH, dim / 55, rnd);

    // edge fade: the drawing dissolves toward the border, like a real sketch
    var fadeN = sampleNoise(outW, outH, dim / 9, rnd);

    var out = makeCanvas(outW, outH);
    var ctx = out.getContext('2d');
    var id = ctx.createImageData(outW, outH);
    var d = id.data;
    var paper = [244, 236, 221];
    var ink = P.ink;
    var th = P.hatch;
    for (var y = 0; y < outH; y++) {
      var ny = y / (outH - 1);
      for (var x = 0; x < outW; x++) {
        var i = y * outW + x;
        var nx = x / (outW - 1);
        var dv = D[i];
        var hatch =
          H1[i] * smoothstep(th[0] - 0.1, th[0] + 0.12, dv) +
          H2[i] * smoothstep(th[1] - 0.1, th[1] + 0.12, dv) +
          H3[i] * smoothstep(th[2] - 0.08, th[2] + 0.12, dv);
        hatch = clamp01(hatch * P.hatchGain);
        var tone = clamp01(dv * P.toneGain);
        // subject emphasis: hatching and tone thin out toward the edges,
        // the way an artist leaves the background loosely suggested
        var rx = (nx - 0.5) / 0.62, ry = (ny - 0.45) / 0.7;
        var focus = 1 - 0.6 * smoothstep(0.45, 1.05, Math.sqrt(rx * rx + ry * ry));
        hatch *= focus;
        tone *= focus;
        var line = S[i];
        var grain = tooth[i];
        var g = 0.76 + 0.24 * grain;
        var a = 1 - (1 - line * 0.95) * (1 - hatch * g) * (1 - tone * (0.55 + 0.45 * grain));
        // edge vignette with a slightly ragged boundary
        var ex = Math.min(nx, 1 - nx), ey = Math.min(ny, 1 - ny);
        var edge = Math.min(ex * 1.15, ey);
        var fade = smoothstep(0.015, 0.12 + 0.05 * fadeN[i], edge);
        a = clamp01(a * fade * g * 1.08) * P.inkMax;
        var fibre = (fib[i] - 0.5) * 10 + (grain - 0.5) * 9;
        var p = i * 4;
        d[p] = paper[0] + fibre + (ink[0] - paper[0]) * a;
        d[p + 1] = paper[1] + fibre + (ink[1] - paper[1]) * a;
        d[p + 2] = paper[2] + fibre * 0.9 + (ink[2] - paper[2]) * a;
        d[p + 3] = 255;
      }
    }
    ctx.putImageData(id, 0, 0);
    return out;
  }

  function grainPlane(w, h, rnd, baseRes) {
    var scale = Math.min(1, baseRes / Math.max(w, h));
    var gw = Math.max(8, Math.round(w * scale)), gh = Math.max(8, Math.round(h * scale));
    var g = new Float32Array(gw * gh);
    for (var i = 0; i < g.length; i++) g[i] = (rnd() + rnd()) / 2;
    if (gw === w && gh === h) return g;
    return bilinearPlaneTo(g, gw, gh, w, h);
  }

  function sampleNoise(w, h, cell, rnd) {
    cell = Math.max(2, cell);
    var gw = Math.ceil(w / cell) + 2, gh = Math.ceil(h / cell) + 2;
    var grid = new Float32Array(gw * gh);
    for (var i = 0; i < grid.length; i++) grid[i] = rnd();
    return sampleGrid(grid, gw, gh, w, h, cell);
  }

  var idCounter = 0;
  function tagSource(canvas) {
    idCounter += 1;
    canvas.__cxId = 'src' + idCounter + '-' + Date.now();
    return canvas;
  }

  global.CXPhotoFX = {
    retroFilm: retroFilm,
    oilPaint: oilPaint,
    oilPaintProgressive: oilPaintProgressive,
    sketch: sketch,
    SKETCH_STYLES: SKETCH_STYLES,
    fitCanvas: fitCanvas,
    resample: resample,
    makeCanvas: makeCanvas,
    tagSource: tagSource,
    mulberry32: mulberry32,
  };
})(window);
